import React, { useState } from "react";
import toast from "react-hot-toast";
import {
  Plus,
  SquarePen,
  Trash2,
  FileText,
  Calendar,
  Eye,
} from "lucide-react";

import {
  useGetAdminArticlesQuery,
  useDeleteArticleMutation,
  Article
} from "../../../../store/apis/helpCenter.api";

import {
  ConfigurableTable,
  Button,
  SearchInput,
  ConfirmationModal,
  SimpleTooltip,
  Pagination,
  Select,
  Badge
} from "../../../common";

import { TableColumn } from "../../../common/Table.tsx";
import ArticleModal from "../modals/ArticleModal";
import FilterWrapper from "../../../common/FilterWrapper";
import { useHasPermission } from "../../../../store/hooks/useRbac";
import { PERMISSIONS } from "../../../../utils/rbac/permissions";
import { ArticleTableSkeleton } from "../Skeleton";

// Extend type to include backend _id
type ArticleWithId = Article & { _id: string };

const ManageArticles: React.FC = () => {
  const canManage = useHasPermission(PERMISSIONS.HELP_CENTER_ADMIN_MANAGE || "HELP_CENTER_ADMIN_VIEW");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [publishStatus, setPublishStatus] = useState<string | undefined>(undefined);

  const { data, isLoading, isFetching } = useGetAdminArticlesQuery({
    page,
    limit,
    search: searchTerm || undefined,
    isPublished: publishStatus === "published" ? true : publishStatus === "draft" ? false : undefined,
  });

  const articles: ArticleWithId[] = (data as any)?.data?.data || [];
  const total = (data as any)?.data?.total || 0;

  const [deleteArticle, { isLoading: isDeleting }] = useDeleteArticleMutation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalViewMode, setModalViewMode] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<ArticleWithId | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleEdit = (article: ArticleWithId) => {
    setSelectedArticle(article);
    setModalViewMode(false);
    setIsModalOpen(true);
  };

  const handleView = (article: ArticleWithId) => {
    setSelectedArticle(article);
    setModalViewMode(true);
    setIsModalOpen(true);
  };

  // 🗑 Delete
  const handleDelete = async () => {
    if (deleteId) {
      try {
        await deleteArticle(deleteId).unwrap();
        toast.success("Article deleted successfully");
      } catch (error) {
        console.error("Failed to delete article:", error);
        toast.error("Failed to delete article");
      } finally {
        setDeleteId(null);
      }
    }
  };

  // 📊 Table columns
  const columns: TableColumn<ArticleWithId>[] = [
    {
      key: "title",
      title: "ARTICLE TITLE",
      label: "Article Title",
      required: true,
      isReorderable: false,
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-lg text-blue-600 flex-shrink-0">
            <FileText className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <SimpleTooltip label={record.title} side="top">
              <span className="font-semibold text-slate-900 block truncate">
                {record.title}
              </span>
            </SimpleTooltip>
          </div>
        </div>
      ),
    },
    {
      key: "isPublished",
      title: "STATUS",
      label: "Status",
      required: true,
      isReorderable: false,
      render: (_, record) => (
        <Badge variant={record.isPublished ? "green" : "gray"} size="middle">
          {record.isPublished ? "Published" : "Draft"}
        </Badge>
      ),
    },
    {
      key: "updatedAt",
      title: "LAST UPDATED",
      label: "Last Updated",
      dataIndex: "updatedAt",
      render: (val) => {
        if (!val) return "-";
        const date = new Date(val);
        const d = String(date.getDate()).padStart(2, '0');
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const y = date.getFullYear();
        return (
          <div className="flex items-center gap-2 text-slate-500">
            <Calendar className="w-3.5 h-3.5" />
            <span className="text-sm">{`${d}/${m}/${y}`}</span>
          </div>
        );
      },
    },
    {
      key: "actions",
      title: "ACTIONS",
      label: "Actions",
      required: true,
      isReorderable: false,
      render: (_, record) => (
        <div className="flex items-center space-x-2">
          <SimpleTooltip label="Preview">
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.currentTarget.blur();
                handleView(record);
              }}
              aria-label="Preview article"
              className="text-blue-600 hover:text-blue-800 transition-colors"
            >
              <Eye className="w-4 h-4" aria-hidden="true" />
            </button>
          </SimpleTooltip>

          <SimpleTooltip label="Edit">
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.currentTarget.blur();
                handleEdit(record);
              }}
              aria-label="Edit"
              className="text-green-600 hover:text-green-800 transition-colors"
            >
              <SquarePen className="w-4 h-4" aria-hidden="true" />
            </button>
          </SimpleTooltip>

          <SimpleTooltip label="Delete">
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.currentTarget.blur();
                setDeleteId(record._id);
              }}
              aria-label="Delete"
              className="text-red-600 hover:text-red-800 transition-colors"
            >
              <Trash2 className="w-4 h-4" aria-hidden="true" />
            </button>
          </SimpleTooltip>
        </div>
      ),
    },
  ];

  // 🎨 Modal control from parent via event
  React.useEffect(() => {
    const handleOpenModal = () => {
      setSelectedArticle(null);
      setModalViewMode(false);
      setIsModalOpen(true);
    };
    window.addEventListener('open-article-modal', handleOpenModal);
    return () => window.removeEventListener('open-article-modal', handleOpenModal);
  }, []);

  return (
    <div className="space-y-6">
      <FilterWrapper>
        <div className="flex flex-col md:flex-row items-end gap-4">
          <div className="w-full md:w-96">
            <label className="block text-sm font-semibold text-slate-700 mb-1">Search</label>
            <SearchInput
              value={searchTerm}
              onChange={(val) => {
                setSearchTerm(val);
                setPage(1);
              }}
              placeholder="Search articles..."
            />
          </div>
          <div className="w-full md:w-64">
            <label className="block text-sm font-semibold text-slate-700 mb-1">Status</label>
            <Select
              value={publishStatus}
              onChange={(val) => {
                setPublishStatus(val as string | undefined);
                setPage(1);
              }}
              options={[
                { value: "", label: "All Status" },
                { value: "published", label: "Published" },
                { value: "draft", label: "Draft" },
              ]}
              placeholder="Filter by Status"
            />
          </div>
        </div>
      </FilterWrapper>

      <div className="bg-white rounded-xl shadow-soft border border-slate-100 overflow-hidden">
        <ConfigurableTable
          columns={columns}
          data={articles}
          loading={isLoading || isFetching}
          skeleton={<ArticleTableSkeleton />}
          rowKey="_id"
          spacing={0}
          configOptions={{ persistenceKey: "help-center-articles" }}
          renderColumnSelector={(selector) => (
            <div className="px-6 py-4 border-b border-slate-200 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="flex flex-col">
                <h3 className="text-lg font-bold text-slate-900">Help Center Articles ({total})</h3>
              </div>

              <div className="flex items-center gap-3 w-full lg:w-auto justify-end">
                {selector}
              </div>
            </div>
          )}
        />
      </div>

      <div className="mt-4">
        <Pagination
          currentPage={page}
          totalItems={total}
          itemsPerPage={limit}
          onPageChange={setPage}
          onItemsPerPageChange={(l) => {
            setLimit(l);
            setPage(1);
          }}
        />
      </div>

      {/* Modal */}
      <ArticleModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setModalViewMode(false);
        }}
        article={selectedArticle}
        viewMode={modalViewMode}
        onRequestEdit={(a) => {
          setSelectedArticle(a as ArticleWithId);
          setModalViewMode(false);
        }}
      />

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Article"
        message="Are you sure you want to delete this article? This action cannot be undone."
        isLoading={isDeleting}
        type="danger"
      />
    </div>
  );
};

export default ManageArticles;
